import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { PageHeader } from '@/components/AIBadge';
import { CLIENT } from '@/constants/testIds';
import { useAuth } from '@/context/AuthContext';
import api, { apiErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import {
    Layers, FileText, CreditCard, Check, Loader2,
    Calendar, ShieldCheck, AlertTriangle, Info, DollarSign, ArrowRight, X,
} from 'lucide-react';

const money = (n) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(n || 0);

const RISK_META = {
    low: { label: 'Bajo', color: '#00875A' },
    medium: { label: 'Medio', color: '#F59E0B' },
    high: { label: 'Alto', color: '#EF4444' },
};

export default function NewRequest() {
    const { user } = useAuth();
    const role = user?.role || 'client';
    const isProvider = role === 'provider';
    const basePath = isProvider ? '/provider' : '/client';
    const accent = isProvider ? '#E08B3C' : '#1F8FB8';
    const title = isProvider ? 'Nuevo distintivo de proveedor' : 'Nueva solicitud de distintivo';
    const subtitle = isProvider ? '◆ Panel Proveedor' : '◆ Panel Cliente';

    const navigate = useNavigate();
    const [distintivos, setDistintivos] = useState([]);
    const [distintivosError, setDistintivosError] = useState(false);
    const [rubros, setRubros] = useState([]);
    const [loading, setLoading] = useState(true);

    const [rubroId, setRubroId] = useState('');
    const [paymentMode, setPaymentMode] = useState('monthly');
    const [notes, setNotes] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        Promise.all([
            api.get('/rubros'),
            // Iter 71 · distintivo catálogo completo — ahora es la fuente para el paso 1 del wizard.
            api.get('/public/distintivos').catch(() => null),
        ]).then(([r, d]) => {
            setRubros((r.data || []).sort((a, b) => (a.name || '').localeCompare(b.name || '')));
            if (d) {
                setDistintivos(d.data || []);
            } else {
                setDistintivosError(true);
            }
        }).catch((e) => toast.error(apiErrorMessage(e, 'Error al cargar catálogos')))
            .finally(() => setLoading(false));
    }, []);

    // Preseleccionar rubro por industry del usuario
    useEffect(() => {
        if (!user?.industry || rubroId || rubros.length === 0) return;
        const target = user.industry.trim().toLowerCase();
        const found = rubros.find((r) => (r.name || '').toLowerCase() === target);
        if (found) setRubroId(found.id);
    }, [user?.industry, rubros, rubroId]);

    const rubroSelected = useMemo(() => rubros.find((r) => r.id === rubroId), [rubros, rubroId]);
    // Iter 73 · el precio ahora depende del rango de tamaño del rubro
    // (habitaciones/mesas/colaboradores/m²) — se consulta al backend porque
    // el cálculo usa el dato de tamaño del perfil del cliente + posible
    // override manual (custom_price_mxn), ninguno de los dos vive en el
    // frontend.
    const [quote, setQuote] = useState(null); // {price_mxn, pricing_metric, size_field, size_value}
    const [quoteLoading, setQuoteLoading] = useState(false);
    useEffect(() => {
        if (!rubroId) { setQuote(null); return; }
        setQuoteLoading(true);
        api.get('/pricing/my-quote', { params: { rubro_id: rubroId } })
            .then((r) => setQuote(r.data))
            .catch(() => setQuote(null))
            .finally(() => setQuoteLoading(false));
    }, [rubroId]);

    // Iter 71 · el distintivo se elige EXPLÍCITAMENTE dentro de este wizard,
    // por solicitud — ya no se lee de user.desired_distintivo_ids ("Mi
    // distintivo" en el panel sigue existiendo solo como historial/referencia).
    const [selectedDistintivoIds, setSelectedDistintivoIds] = useState([]);
    const toggleDistintivo = (id) => {
        setSelectedDistintivoIds((prev) => (
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        ));
    };

    const submit = async (e) => {
        e.preventDefault();
        if (selectedDistintivoIds.length === 0) return toast.error('Selecciona al menos un distintivo para esta solicitud');
        if (!rubroId) return toast.error('Selecciona el rubro de tu empresa');
        setSubmitting(true);
        try {
            await api.post('/certifications', {
                desired_distintivo_ids: selectedDistintivoIds,
                industry: rubroSelected?.name || '',
                notes,
                rubro_id: rubroId,
                payment_mode: paymentMode,
            });
            toast.success('Solicitud creada con éxito');
            navigate(basePath);
        } catch (err) {
            toast.error(apiErrorMessage(err, 'Error al crear solicitud'));
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <DashboardLayout role={role}>
                <div className="p-12 text-center opacity-60"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout role={role}>
            <PageHeader title={title} subtitle={subtitle} accent={accent} />

            <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="new-request-form">
                {/* Columna izquierda: pasos */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Paso 1: Distintivo(s) — elección independiente por solicitud (Iter 71) */}
                    <Section number="1" title="¿Qué distintivo(s) quieres solicitar?" icon={ShieldCheck} accent={accent}>
                        <p className="text-xs text-gray-500 mb-3">
                            Elige uno o más distintivos para <b>esta</b> solicitud. Puede ser distinto a los que ya
                            tienes guardados en tu panel principal — ahí solo queda tu historial de referencia.
                        </p>
                        {selectedDistintivoIds.length > 0 && (
                            <div className="mb-3 flex flex-wrap gap-2" data-testid="new-request-distintivo-selected">
                                {distintivos.filter((d) => selectedDistintivoIds.includes(d.id)).map((d) => (
                                    <span key={d.id} data-testid={`new-request-distintivo-chip-${d.code}`}
                                        className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-bold"
                                        style={{ background: accent, color: '#fff' }}>
                                        {d.code}
                                        <button type="button" onClick={() => toggleDistintivo(d.id)} className="hover:opacity-70">
                                            <X className="w-3 h-3" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                        {distintivos.length === 0 ? (
                            distintivosError ? (
                                <div className="text-sm p-3 border-l-4 flex items-start gap-2" style={{ borderColor: '#DC2626', background: 'rgba(220,38,38,0.06)' }} data-testid="new-request-distintivos-error">
                                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" style={{ color: '#DC2626' }} />
                                    <span>No se pudo cargar el catálogo de distintivos. <button type="button" onClick={() => window.location.reload()} className="underline font-bold">Reintentar</button></span>
                                </div>
                            ) : (
                                <div className="text-sm text-gray-500">Cargando distintivos disponibles...</div>
                            )
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="new-request-distintivos-grid">
                                {distintivos.filter((d) => d.is_active !== false).map((d) => {
                                    const isComingSoon = !!d.coming_soon;
                                    const active = selectedDistintivoIds.includes(d.id);
                                    return (
                                        <button key={d.id} type="button" disabled={isComingSoon}
                                            onClick={() => toggleDistintivo(d.id)}
                                            data-testid={`new-request-distintivo-option-${d.code}`}
                                            title={isComingSoon ? 'Estará disponible próximamente' : ''}
                                            className="text-left px-4 py-3 border-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                            style={{
                                                borderColor: active ? accent : 'var(--border)',
                                                background: active ? `${accent}12` : 'transparent',
                                            }}>
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-1.5">
                                                        <span className="font-bold text-sm truncate">{d.code}</span>
                                                        {isComingSoon && (
                                                            <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5"
                                                                style={{ background: 'rgba(245,158,11,0.2)', color: '#B45309' }}>
                                                                Próximamente
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="text-xs text-gray-500 line-clamp-2">{d.name}</div>
                                                </div>
                                                {active && <Check className="w-4 h-4 shrink-0" style={{ color: accent }} />}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </Section>

                    {/* Paso 2: Rubro */}
                    <Section number="2" title="Selecciona el rubro de tu empresa" icon={Layers} accent={accent}>
                        <select value={rubroId}
                            onChange={(e) => setRubroId(e.target.value)}
                            required
                            data-testid="new-request-rubro"
                            className="w-full border-2 p-3 outline-none focus:border-black"
                            style={{ borderColor: 'var(--border)', background: 'transparent' }}>
                            <option value="">— Selecciona tu rubro —</option>
                            {rubros.map((r) => (
                                <option key={r.id} value={r.id}>
                                    {r.name} · {r.category}
                                </option>
                            ))}
                        </select>
                        {rubroSelected && (
                            <div className="mt-2 flex items-center gap-2 text-xs">
                                <span className="opacity-60">Nivel de riesgo:</span>
                                <span className="font-bold px-2 py-0.5"
                                    style={{
                                        background: `${RISK_META[rubroSelected.risk_level]?.color || '#7C6BEF'}15`,
                                        color: RISK_META[rubroSelected.risk_level]?.color || '#7C6BEF',
                                    }}>
                                    {RISK_META[rubroSelected.risk_level]?.label || 'Medio'}
                                </span>
                            </div>
                        )}
                    </Section>

                    {/* Paso 3: Modalidad */}
                    <Section number="3" title="Modalidad de pago" icon={CreditCard} accent={accent}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <PayModeCard
                                mode="monthly"
                                active={paymentMode === 'monthly'}
                                onClick={() => setPaymentMode('monthly')}
                                title="Iguala mensual"
                                subtitle="Pago recurrente antes de cada auditoría"
                                highlight="Flexibilidad total"
                                testid="paymode-monthly"
                            />
                            <PayModeCard
                                mode="upfront"
                                active={paymentMode === 'upfront'}
                                onClick={() => setPaymentMode('upfront')}
                                title="Pago único"
                                subtitle="Pago único de las 8 auditorías del ciclo"
                                highlight="Con descuento"
                                testid="paymode-upfront"
                            />
                        </div>
                    </Section>

                    {/* Paso 4: Notas */}
                    <Section number="4" title="Contexto adicional (opcional)" icon={FileText} accent={accent}>
                        <textarea value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={3}
                            data-testid={CLIENT.notesInput}
                            placeholder="Describe brevemente tu operación, tamaño, o cualquier detalle relevante..."
                            className="w-full border-2 p-3 outline-none focus:border-black resize-none"
                            style={{ borderColor: 'var(--border)', background: 'transparent' }} />
                    </Section>
                </div>

                {/* Columna derecha: cotización sticky */}
                <div className="lg:col-span-1">
                    <div className="lg:sticky lg:top-4">
                        <QuoteCard
                            quote={quote}
                            quoteLoading={quoteLoading}
                            rubro={rubroSelected}
                            distintivos={distintivos.filter((d) => selectedDistintivoIds.includes(d.id))}
                            accent={accent}
                        />
                        <div className="mt-3 flex flex-col gap-2">
                            <button type="submit"
                                disabled={submitting || selectedDistintivoIds.length === 0 || !rubroId}
                                data-testid={CLIENT.submitRequest}
                                className="text-white px-6 py-3 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50"
                                style={{ background: accent }}>
                                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                                {submitting ? 'Enviando...' : 'Confirmar solicitud'}
                                <ArrowRight className="w-4 h-4" />
                            </button>
                            <button type="button" onClick={() => navigate(basePath)}
                                className="border-2 px-6 py-3 font-medium"
                                style={{ borderColor: 'var(--border)' }}>
                                Cancelar
                            </button>
                        </div>
                    </div>
                </div>
            </form>
        </DashboardLayout>
    );
}

function Section({ number, title, icon: Icon, accent, children }) {
    return (
        <div className="border-2 p-5" style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}>
            <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 flex items-center justify-center text-white font-bold text-sm"
                    style={{ background: accent }}>
                    {number}
                </div>
                <Icon className="w-5 h-5" style={{ color: accent }} />
                <h3 className="font-display font-black text-lg">{title}</h3>
            </div>
            {children}
        </div>
    );
}

function PayModeCard({ mode, active, onClick, title, subtitle, highlight, testid }) {
    return (
        <button type="button" onClick={onClick}
            data-testid={testid}
            className="text-left p-4 border-2 relative"
            style={{
                borderColor: active ? '#7C6BEF' : 'var(--border)',
                background: active ? 'rgba(124,107,239,0.08)' : 'transparent',
                borderWidth: active ? '3px' : '2px',
            }}>
            <div className="flex items-center gap-2 mb-1">
                {mode === 'monthly' ? <Calendar className="w-4 h-4" /> : <DollarSign className="w-4 h-4" />}
                <span className="font-display font-black text-base">{title}</span>
                {active && <Check className="w-4 h-4 ml-auto" style={{ color: '#7C6BEF' }} />}
            </div>
            <p className="text-xs opacity-70 mb-2">{subtitle}</p>
            <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: '#00875A' }}>
                {highlight}
            </div>
        </button>
    );
}

// Iter 73 · cotización simplificada: un solo número, calculado por el
// backend según el rango de tamaño del rubro (o el override manual del
// cliente si el admin lo definió). Muestra el mismo mensaje neutral tanto
// si falta el dato de tamaño del cliente como si el rango correspondiente
// sigue sin precio — el cliente nunca ve la causa exacta.
function QuoteCard({ quote, quoteLoading, rubro, distintivos, accent }) {
    if (!rubro) {
        return (
            <div className="p-5 border-2 text-center" style={{ borderColor: 'var(--border)' }} data-testid="quote-select-rubro-card">
                <Info className="w-8 h-8 mx-auto mb-2 opacity-40" />
                <div className="text-sm opacity-70">Selecciona tu rubro para ver el precio</div>
            </div>
        );
    }
    if (quoteLoading) {
        return (
            <div className="p-5 border-2 text-center" style={{ borderColor: 'var(--border)' }} data-testid="quote-loading-card">
                <Loader2 className="w-6 h-6 mx-auto animate-spin opacity-50" />
            </div>
        );
    }
    const priceAvailable = quote && quote.price_mxn != null;
    if (!priceAvailable) {
        const needsProfileData = quote && quote.size_value == null;
        return (
            <div className="p-5 border-2 text-center" style={{ borderColor: 'var(--border)' }} data-testid="quote-pending-card">
                <Info className="w-8 h-8 mx-auto mb-2 opacity-40" />
                <div className="text-sm font-bold mb-1">Cotización disponible próximamente para tu rubro</div>
                <p className="text-xs opacity-70">
                    Envía tu solicitud con normalidad — nuestro equipo te compartirá el precio antes de confirmar la auditoría.
                </p>
                {needsProfileData && (
                    <Link to="/client" className="text-xs font-bold underline mt-2 inline-block" style={{ color: accent }}
                        data-testid="quote-complete-profile-link">
                        Completa el dato de tamaño de tu negocio en tu perfil
                    </Link>
                )}
            </div>
        );
    }
    return (
        <div className="border-2 overflow-hidden" style={{ borderColor: accent, background: 'var(--card-bg)' }} data-testid="quote-summary">
            <div className="p-4 text-white" style={{ background: accent }}>
                <div className="text-[10px] uppercase tracking-widest font-bold opacity-90">Cotización</div>
                <div className="text-lg font-display font-black">{rubro.name}</div>
            </div>
            <div className="p-5 space-y-3">
                {distintivos.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                        {distintivos.map((d) => (
                            <span key={d.id} className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold"
                                style={{ background: `${accent}18`, color: accent }}>
                                {d.code}
                            </span>
                        ))}
                    </div>
                )}
                <div className="flex items-baseline justify-between gap-2">
                    <span className="text-xs opacity-80">Precio por auditoría</span>
                    <span className="font-mono font-black text-lg" style={{ color: accent }}>
                        {money(quote.price_mxn)}
                    </span>
                </div>
            </div>
        </div>
    );
}
